import React, { FC, useEffect, useState } from 'react';
import './MediaPreview.css';
import Lightbox from '../Lightbox/Lightbox';
import { MediaItem, MediaType, MediaURL, UploadedMedia } from '@/Shared/Types';
import { MessageHelper } from '@/Shared/Helpers';
import { FaPen, IoClose } from '@/Shared/Lib/UI/Icons';
import { DeleteButton } from '../Buttons';
import FrameView from '../FrameView/FrameView';
import InputField from '../../Forms/Fields/InputField/InputField';
import { useFormikContext } from '@/Shared/Lib/Logic';
import { ConfirmHook } from '@/Shared/Logic/Hooks/TechnicalHooks/useConfirm';
import { LightBoxHook } from '@/Shared/Logic/Hooks/TechnicalHooks/useLightBox';

interface MediaPreviewProps<T> {

	medias: (UploadedMedia | MediaURL)[],
	type: MediaType,
	confirmHook : ConfirmHook;
	lightBoxHook : LightBoxHook
}

const MediaPreview: FC<MediaPreviewProps<any>> = ({ type, medias, confirmHook, lightBoxHook }) => {
	const { setFieldValue } = useFormikContext();
	const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
	

	useEffect(() => {

		const items = medias.map((file, index) => {
			const currentItem = { type } as MediaItem;
			currentItem.id = index;

			if (file.small) {
				currentItem.small = file.small instanceof File ? URL.createObjectURL(file.small) : file.small;
			}

			if (file instanceof UploadedMedia) {
				currentItem.large = file.large instanceof File ? URL.createObjectURL(file.large) : file.large;
			}

			else if (file instanceof MediaURL) {
				currentItem.large = file.url;
			}

			return currentItem;
		});
		setMediaItems(items);
	}, [medias]);

	const revokeObjectURL = (item: MediaItem) => {
		const smallURL = item.small;
		const largeURL = item.large;

		smallURL && URL.revokeObjectURL(smallURL);
		largeURL && URL.revokeObjectURL(largeURL);
	}

	const handleDelete = (item: MediaItem) => {
		const filtered = mediaItems.filter(media => item.id !== media.id);
		setFieldValue(`${type}s`, filtered, true);
		revokeObjectURL(item);
	};

	const handleDeleteItem = async (itemToDelete: MediaItem) => {
		const confirmed = await confirmHook.openConfirm?.({
			title: MessageHelper.MODAL.CONFIRM.TITLE,
			content: MessageHelper.MODAL.CONFIRM.REMOVING(type),
		});
		if (confirmed) {
			handleDelete(itemToDelete);
		}
	}

	return (
		<fieldset className='MediaPreview height__fit-content' data-testid='MediaPreview'>
			
			<legend>{type === 'image' && 'Images - Preview'} {type === 'video' && 'Vidéos - Preview'}</legend>
			<div className='Gallery'>
				{mediaItems.map((item, index) => (
					<div className='Gallery-item__wrapper elevation-1' key={item.id ?? index} >
						<div className='Gallery-item' onClick={() => {
							lightBoxHook.openLightBox({
									medias : mediaItems,
									initialIndex : index
								});
							}}>
							{item.type === 'image' && <img src={item.small || item.large} alt={item.caption || item.small || "Image du bien"} />}
							{item.type === 'video' && <FrameView url={item.large} />}
						</div>
						<InputField
							label={item.type === 'image' ? "Titre de l'image" : "Titre de la vidéo"}
							icon={<FaPen />}
							name={`images[${index}].caption`}
							placeholder={item.type === 'image' ? "Saisir un titre pour l'image" : "Saisir un titre pour la vidéo"}
							autoComplete='on' />
						{<DeleteButton 
						classNames='MediaPreview__delete-button'  
						onClick={() => handleDeleteItem(item)} icon={<IoClose />} 
						tooltip="Bouton de retrait d'élément"
						/>}
					</div>
				))}
				{mediaItems.length === 0 && <div>Aucun media à afficher.</div>}
			</div>
			
		</fieldset>
	);
};

export default MediaPreview;